import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MergeCellsOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { Button, Form, Popconfirm, Select, Spin, Table, Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { pumpShiftApi } from '../../../../../apis'
import {
  AlertMessageEnum,
  getDateFormat,
  getPumpShiftStatus,
} from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { PumpShiftEntity, PumpShiftStatusEnum } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadProfile,
  loadPumpShifts,
  loadStations,
  setActionProcessing,
  setAlert,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const FooterElement = styled.div`
  margin-right: 20px;
  font-weight: bold;
`

type ManagePumpShiftModalComponentProps = {
  pumpShiftId: number
}

export const ManagePumpShiftModalComponent: React.FC<ManagePumpShiftModalComponentProps> =
  ({ pumpShiftId }) => {
    const dispatch = useDispatch()
    const { language } = useSelector((state: RootState) => state.language)
    const { management } = useSelector((state: RootState) => state.common)
    const { shifts } = useSelector((state: RootState) => state.shift)
    const { pumpShifts } = useSelector((state: RootState) => state.pumpShift)
    const { stations, isStationsLoaded } = useSelector(
      (state: RootState) => state.station
    )

    const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

    const [selectedStationId, setSelectedStationId] = React.useState(-1)
    const [unclosePumpShifts, setUnclosePumpShifts] = React.useState<
      PumpShiftEntity[]
    >([])
    const [totalVolume, setTotalVolume] = React.useState(0)
    const [totalAmount, setTotalAmount] = React.useState(0)
    const [pumpShiftsLoading, setPumpShiftLoading] = React.useState(false)
    const [currentTime, setCurrentTime] = React.useState(new Date().getTime())

    React.useEffect(() => {
      const timer = setTimeout(() => {
        setCurrentTime(currentTime + 5000)
      }, 5000)
      return () => clearTimeout(timer)
    }, [currentTime])

    React.useEffect(() => {
      if (pumpShiftId === -1) {
        dispatch(loadStations())
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
      if (stations.length) {
        setSelectedStationId(stations[0].id)
      }
    }, [stations])

    React.useEffect(() => {
      if (selectedStationId !== -1) {
        const loadPumpShifts = async () => {
          try {
            setPumpShiftLoading(true)
            const resp = await pumpShiftApi.getPumpShifts({
              stationIds: [selectedStationId],
              statuses: [PumpShiftStatusEnum.UNCLOSE],
              stationName: '',
              pumpName: '',
              shiftName: '',
              pageSize: 1000,
              pageIndex: 1,
            })
            setUnclosePumpShifts(resp.data.data)
            setTotalAmount(resp.data.totalAmount)
            setTotalVolume(resp.data.totalVolume)
          } catch (e) {
            dispatch(
              setAlert({
                key: 'pumpShift.loadDataFail',
                type: AlertMessageEnum.ERROR,
              })
            )
            setUnclosePumpShifts([])
          } finally {
            setPumpShiftLoading(false)
          }
        }
        loadPumpShifts()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStationId])

    const columns = React.useMemo(
      () => [
        {
          dataIndex: 'id',
          title: translate('pumpShift.id', language),
          width: 50,
        },
        {
          title: translate('pumpShift.createdDate', language),
          width: 50,
          render: (_: any, pumpShift: PumpShiftEntity) =>
            getDateFormat(pumpShift.createdDate),
        },
        {
          title: translate('pumpShift.shift', language),
          width: 50,
          render: (_: any, pumpShift: PumpShiftEntity) => pumpShift.shift.name,
        },
        {
          title: translate('pumpShift.pump', language),
          width: 70,
          render: (_: any, pumpShift: PumpShiftEntity) => pumpShift.pump.name,
        },
      ],
      [language]
    )

    const [pumpShift, setPumpShift] = React.useState(() => {
      const index = pumpShifts.map((item) => item.id).indexOf(pumpShiftId)
      return index > -1 ? pumpShifts[index] : undefined
    })

    React.useEffect(() => {
      if (pumpShiftId > -1) {
        const loadPumpShift = async () => {
          try {
            setPumpShiftLoading(true)
            const resp = await pumpShiftApi.getPumpShiftById(pumpShiftId)
            setPumpShift(resp.data)
          } catch (e) {
            dispatch(
              setAlert({
                key: 'pumpShift.loadDataFail',
                type: AlertMessageEnum.ERROR,
              })
            )
          } finally {
            setPumpShiftLoading(false)
          }
        }
        loadPumpShift()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [title] = React.useState(
      pumpShiftId === -1
        ? translate('pumpShift.closeShiftByStation', language)
        : undefined
    )

    const status = React.useMemo(
      () =>
        pumpShift
          ? getPumpShiftStatus(pumpShift, shifts, currentTime)
          : PumpShiftStatusEnum.WORKING,
      [currentTime, pumpShift, shifts]
    )

    const confirmClosePumpShift = async () => {
      try {
        dispatch(setAlert())
        dispatch(setActionProcessing(true))
        if (pumpShift) {
          await pumpShiftApi.closePumpShift(pumpShift.id)
        } else if (pumpShiftId === -1 && selectedStationId !== -1) {
          await pumpShiftApi.closePumpShiftsByStation(selectedStationId)
        }
        dispatch(loadPumpShifts())
        dispatch(
          setAlert({
            key: 'pumpShift.closePumpShift.success',
            type: AlertMessageEnum.SUCCESS,
          })
        )
      } catch (e) {
        if (e && e.response && e.response.status === 401) {
          dispatch(loadProfile())
          if (pumpShift) {
            await pumpShiftApi.closePumpShift(pumpShift.id)
          } else if (pumpShiftId === -1 && selectedStationId !== -1) {
            await pumpShiftApi.closePumpShiftsByStation(selectedStationId)
          }
          dispatch(loadPumpShifts())
          dispatch(
            setAlert({
              key: 'pumpShift.closePumpShift.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        }
        dispatch(
          setAlert({
            key: 'pumpShift.closePumpShift.fail',
            type: AlertMessageEnum.ERROR,
          })
        )
      } finally {
        dispatch(setActionProcessing(false))
      }
    }

    const body = (
      <Form>
        {(!management.alertMessage ||
          (management.alertMessage &&
            management.alertMessage.type !== AlertMessageEnum.SUCCESS)) && (
          <>
            {pumpShiftId === -1 ? (
              <>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('pumpShift.station', language)}
                    </StyledLabel>
                  }
                >
                  <Select
                    placeholder={translate('pumpShift.station', language)}
                    style={{ width: '100%' }}
                    loading={!isStationsLoaded}
                    value={
                      selectedStationId !== -1 ? selectedStationId : undefined
                    }
                    disabled={!isStationsLoaded}
                    onChange={(e: any) => {
                      e && setSelectedStationId(parseInt(e))
                      dispatch(setAlert())
                    }}
                  >
                    {stations.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {`${item.name}`}
                      </Select.Option>
                    ))}
                  </Select>
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('pumpShift.unclosePumpShifts', language)}
                    </StyledLabel>
                  }
                >
                  {!unclosePumpShifts.length &&
                    !pumpShiftsLoading &&
                    translate('pumpShift.unclosePumpShifts.empty', language)}
                </StyledFormItem>
                {pumpShiftsLoading && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <Spin size="large" />
                  </div>
                )}
                {!pumpShiftsLoading && unclosePumpShifts.length > 0 && (
                  <Table
                    dataSource={unclosePumpShifts.map((item, index) => ({
                      ...item,
                      key: index,
                    }))}
                    columns={columns}
                    bordered
                    pagination={
                      unclosePumpShifts.length > 5
                        ? {
                            position: ['bottomCenter'],
                            showSizeChanger: false,
                            pageSize: 5,
                          }
                        : false
                    }
                    size="small"
                    style={{ marginBottom: '20px' }}
                    scroll={{ x: '40vw' }}
                    footer={() => (
                      <Footer>
                        <FooterElement>
                          {`${translate('pumpShift.totalVolume', language)}: `}
                          <NumberFormat
                            value={totalVolume}
                            suffixKey="common.unitType"
                          />
                        </FooterElement>
                        <FooterElement>
                          {`${translate('pumpShift.totalAmount', language)}: `}
                          <NumberFormat
                            value={totalAmount}
                            suffixKey="common.moneyType"
                          />
                        </FooterElement>
                      </Footer>
                    )}
                  ></Table>
                )}
              </>
            ) : (
              <>
                {pumpShiftsLoading && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <Spin size="large" />
                  </div>
                )}
                {pumpShift && !pumpShiftsLoading && (
                  <>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.id', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.id}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.createdDate', language)}
                        </StyledLabel>
                      }
                    >
                      {getDateFormat(pumpShift.createdDate)}
                    </StyledFormItem>
                    {pumpShift.closedTime && (
                      <StyledFormItem
                        label={
                          <StyledLabel>
                            {translate('pumpShift.closedTime', language)}
                          </StyledLabel>
                        }
                      >
                        {getDateFormat(pumpShift.closedTime, true)}
                      </StyledFormItem>
                    )}
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('common.status', language)}
                        </StyledLabel>
                      }
                    >
                      <Tag
                        color={
                          status === PumpShiftStatusEnum.CLOSED
                            ? 'success'
                            : status === PumpShiftStatusEnum.WORKING
                            ? 'processing'
                            : status === PumpShiftStatusEnum.UNCLOSE
                            ? 'warning'
                            : 'default'
                        }
                        icon={
                          status === PumpShiftStatusEnum.CLOSED ? (
                            <CheckCircleOutlined />
                          ) : status === PumpShiftStatusEnum.WORKING ? (
                            <SyncOutlined spin />
                          ) : status === PumpShiftStatusEnum.UNCLOSE ? (
                            <ExclamationCircleOutlined />
                          ) : (
                            <ClockCircleOutlined />
                          )
                        }
                      >
                        {translate(
                          `pumpShift.status.${status.toLowerCase()}`,
                          language
                        )}
                      </Tag>
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.shift', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.shift.name}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.station', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.pump.tank.station.name}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.tank', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.pump.tank.name}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.pump', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.pump.name}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.fuel', language)}
                        </StyledLabel>
                      }
                    >
                      {pumpShift.pump.tank.fuel.name}
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.totalVolume', language)}
                        </StyledLabel>
                      }
                    >
                      <NumberFormat
                        value={pumpShift.totalVolume || 0}
                        suffixKey="common.unitType"
                      />
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('pumpShift.totalAmount', language)}
                        </StyledLabel>
                      }
                    >
                      <NumberFormat
                        value={pumpShift.totalAmount || 0}
                        suffixKey="common.moneyType"
                      />
                    </StyledFormItem>
                    {pumpShift.executor && (
                      <StyledFormItem
                        label={
                          <StyledLabel>
                            {translate('pumpShift.executer', language)}
                          </StyledLabel>
                        }
                      >
                        {pumpShift.executor.name}
                      </StyledFormItem>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
        <ManageModalFooterComponent
          closeText={status === PumpShiftStatusEnum.CLOSED}
          customButtons={
            <>
              {(selectedStationId || status === PumpShiftStatusEnum.UNCLOSE) &&
                (!management.alertMessage ||
                  management.alertMessage.type !==
                    AlertMessageEnum.SUCCESS) && (
                  <Popconfirm
                    title={translate(
                      'pumpShift.closePumpShift.confirm',
                      language
                    )}
                    okText={translate('common.yes', language)}
                    cancelText={translate('common.no', language)}
                    placement="topRight"
                    onConfirm={confirmClosePumpShift}
                  >
                    {((pumpShiftId === -1 && unclosePumpShifts.length) ||
                      (pumpShift &&
                        status === PumpShiftStatusEnum.UNCLOSE)) && (
                      <Button
                        icon={
                          management.actionProcessing ? (
                            <SyncOutlined spin />
                          ) : (
                            <MergeCellsOutlined />
                          )
                        }
                        type="primary"
                        disabled={management.actionProcessing}
                        onClick={async () => {}}
                      >
                        {translate('pumpShift.closePumpShift', language)}
                      </Button>
                    )}
                  </Popconfirm>
                )}
            </>
          }
        />
      </Form>
    )

    return (
      <ManageModalComponent
        title={title}
        body={body}
        width={isDesktopScreen ? (pumpShiftId !== -1 ? '30%' : '50%') : '100%'}
      />
    )
  }
